import { useEffect, useRef } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useNotifications } from 'components/Notifications/hooks/useNotifications';
import { showNotifications } from 'components/Notifications/utils/showNotification';

import { selectUnreadNotificationsToShow } from 'features/notifications/notificationsSelectors';
import {
  clearReadNotifications,
  clearUnreadNotifications,
  resetUnreadNotificationsToShow
} from 'features/notifications/notificationsSlice';

import {
  INIT_DATE_LOCAL_STORAGE_ITEM_NAME,
  NOTIFICATIONS_CHECK_DELAY_SEC
} from 'constants/uiSettings';

import { useAppDispatch, useAppSelector } from '../../../hooks/useTypedRedux';

dayjs.extend(utc);

const useCheckNotifications = () => {
  const dispatch = useAppDispatch();

  const unreadNotificationsToShow = useAppSelector(
    selectUnreadNotificationsToShow
  );

  const { readNotification, checkUnreadNotifications } = useNotifications();

  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    localStorage.setItem(
      INIT_DATE_LOCAL_STORAGE_ITEM_NAME,
      dayjs.utc().toISOString()
    );

    return () => {
      localStorage.removeItem(INIT_DATE_LOCAL_STORAGE_ITEM_NAME);
      dispatch(clearReadNotifications());
      dispatch(clearUnreadNotifications());
    };
  }, []);

  useEffect(() => {
    checkUnreadNotifications();

    intervalRef.current = setInterval(
      checkUnreadNotifications,
      NOTIFICATIONS_CHECK_DELAY_SEC * 1000
    );

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkUnreadNotifications]);

  useEffect(() => {
    if (!unreadNotificationsToShow?.length) {
      return;
    }

    const initDate = localStorage.getItem(INIT_DATE_LOCAL_STORAGE_ITEM_NAME);

    if (!initDate) {
      return;
    }

    const unreadNotificationsToShowCreatedAfterInit =
      unreadNotificationsToShow.filter((notification) => {
        if (!notification?.createdAt) {
          return false;
        }

        return dayjs.utc(notification.createdAt).isAfter(dayjs.utc(initDate));
      });

    unreadNotificationsToShowCreatedAfterInit.forEach((notification) => {
      showNotifications({ notification, onReadNotification: readNotification });
    });

    dispatch(resetUnreadNotificationsToShow());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadNotificationsToShow]);
};

export { useCheckNotifications };
